import { z } from 'zod';

import {
  FPlatformFragment,
  PlatformCountsQuery,
  PlatformWaypointSendingStrategy,
  UpdatePlatformData,
} from '@dashboard/generated/wp/administrator';

const FormSchema = z
  .strictObject({
    ipPoolName: z.string().nullable(),
    waypointSendingStrategy: z.enum(['DISABLED', 'HIGH_RISK', 'HIGH_VOLUME', 'PAUSED', 'STANDARD', 'SAMPLE']),
    featureFlags: z.array(z.enum(['rawTemplatePreviews', 'legacyTemplates'])),
  })
  .required();

type TInitialFormSchema = z.infer<typeof FormSchema>;

function getPlatformWaypointSendingStrategy(value: unknown): PlatformWaypointSendingStrategy {
  switch (value) {
    case 'DISABLED':
      return PlatformWaypointSendingStrategy.Disabled;
    case 'HIGH_RISK':
      return PlatformWaypointSendingStrategy.HighRisk;
    case 'HIGH_VOLUME':
      return PlatformWaypointSendingStrategy.HighVolume;
    case 'PAUSED':
      return PlatformWaypointSendingStrategy.Paused;
    case 'STANDARD':
      return PlatformWaypointSendingStrategy.Standard;
    case 'SAMPLE':
      return PlatformWaypointSendingStrategy.Sample;
  }
  throw new Error(`Invalid value for 'waypointSendingStrategy'`);
}

export default function parseFormSchema(uData: unknown): UpdatePlatformData | null {
  try {
    const value = FormSchema.parse(uData);
    return {
      ipPoolName: value.ipPoolName === 'default' ? null : value.ipPoolName,
      waypointSendingStrategy: getPlatformWaypointSendingStrategy(value.waypointSendingStrategy),
      featureRawTemplatePreviewEnabled: value.featureFlags.includes('rawTemplatePreviews'),
      featureLegacyTemplatesEnabled: value.featureFlags.includes('legacyTemplates'),
    };
  } catch {
    return null;
  }
}

export function getInitialValues(
  platform: FPlatformFragment,
  platformCount: PlatformCountsQuery['Platform']
): TInitialFormSchema {
  const featureFlags: TInitialFormSchema['featureFlags'] = [];
  if (platform.featureRawTemplatePreviewEnabled) {
    featureFlags.push('rawTemplatePreviews');
  }
  if (platform.featureLegacyTemplatesEnabled) {
    featureFlags.push('legacyTemplates');
  }
  const ipPoolName = platformCount.sesConfiguration.ipPoolName;
  return {
    ipPoolName: ipPoolName ?? 'default',
    waypointSendingStrategy: platform.waypointSendingStrategy,
    featureFlags,
  };
}
